import React, { useState, } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, } from 'reactstrap';
import { useToggle } from 'react-use';
import { toast } from 'react-toastify';
import { parse as parseCsv } from 'papaparse';
import { chunk, has, fromPairs, isEmpty, pickBy, pick, isEqual, keyBy, trim, findKey, } from 'lodash';

import firebase from '../../firebase';
import { batch, getDocumentsData, } from '../../shared/firebase';
import { serial } from '../../shared/util';
import { priceTaxTypes, } from '../../shared/models/product';
import { readCsvFile, productSkuErrors, } from '../../util';
import ExportButton from '../ExportButton';

const db = firebase.firestore();
const { entries, keys, values } = Object;
const emptyValues = { name: '', barcodeInfo: '', itemName: '', requestsNeko: false, inventoryBuffer: null, price: null, priceTaxType: null, };
const formatValue = (field, value) => (({
  requestsNeko: _ => value ? 'はい' : 'いいえ',
  priceTaxType: _ => priceTaxTypes[value]?.label ?? '',
})[field] || (_ => value ?? ''))();
const fetchProducts = async (products) => (await serial(chunk(products, 100), _ => getDocumentsData(_.map(_ => _.ref)))).flat();

export default function ProductsBatchUpdateModal ({ isOpen, onClickClose, onFinish, productsGroupedBySku, exportTargetProducts, showsInventoryBuffer, }) {
  const [fileName, setFileName] = useState('');
  const [items, setItems] = useState(null);
  const [isValidating, toggleValidating] = useToggle(false);
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const columns = {
    name: '商品名',
    barcodeInfo: 'JANまたはバーコード情報',
    itemName: '品名',
    requestsNeko: 'ポスト便希望',
    price: '販売価格',
    priceTaxType: '税区分',
    ...(showsInventoryBuffer && { inventoryBuffer: '在庫バッファ数' }),
  };
  const rowsForExport = async () => {
    return (await fetchProducts(exportTargetProducts)).map(_ => ({
      SKU: _.sku,
      ...fromPairs(entries(columns).map(([k, label]) => [label, formatValue(k, _[k])])),
    }));
  };
  const onSelectFile = async ({ target, target: { files: [file] } }) => {
    if(!file) return;
    setItems(null);
    setFileName(file.name);
    toggleValidating(true);
    try {
      const { data: csvRows } = parseCsv(await readCsvFile(file), { header: true });
      const rows = csvRows.filter(_ => values(_).some(_ => _));
      const skus = rows.map(_ => trim(_.SKU));
      const productsById = keyBy(await fetchProducts(skus.map(_ => productsGroupedBySku[_]?.[0]).filter(_ => _)), 'id');
      setItems(rows.map((row, i) => {
        const product = productsById[productsGroupedBySku[skus[i]]?.[0]?.id];
        const current = { ...pick(emptyValues, keys(columns)), ...pickBy(pick(product, keys(columns)), _ => _ != null) };
        const inputs = fromPairs(entries(columns).filter(([, label]) => has(row, label)).map(([k, label]) => [k, trim(row[label])]));
        const data = {
          ...inputs,
          ...(has(inputs, 'itemName') && { itemName: inputs.itemName || (inputs.name ?? current.name).slice(0, 10) }),
          ...(has(inputs, 'requestsNeko') && { requestsNeko: inputs.requestsNeko === 'はい' }),
          ...(has(inputs, 'inventoryBuffer') && { inventoryBuffer: inputs.inventoryBuffer === '' ? null : Number(inputs.inventoryBuffer) }),
          ...(has(inputs, 'price') && { price: inputs.price === '' ? null : Number(inputs.price.replace(/,/g, '')) }),
          ...(has(inputs, 'priceTaxType') && { priceTaxType: inputs.priceTaxType === '' ? null : (findKey(priceTaxTypes, { label: inputs.priceTaxType }) ?? inputs.priceTaxType) }),
        };
        const price = has(data, 'price') ? data.price : current.price;
        const priceTaxType = price == null ? null : (has(data, 'priceTaxType') ? data.priceTaxType : current.priceTaxType) ?? 'included';
        const dataWithPrice = (has(data, 'price') || has(data, 'priceTaxType')) ? { ...data, priceTaxType } : data;
        const errors = [
          ...productSkuErrors(skus, i, productsGroupedBySku),
          has(data, 'name') && !data.name && '商品名は必須です',
          (data.itemName || '').length > 16 && '品名は16文字以内です',
          data.inventoryBuffer != null && !(Number.isInteger(data.inventoryBuffer) && data.inventoryBuffer >= 0) && '在庫バッファ数は0以上の整数です',
          price != null && !(Number.isInteger(price) && price >= 0) && '販売価格は0以上の整数です',
          priceTaxType != null && !priceTaxTypes[priceTaxType] && '税区分は「税込」または「税抜」です',
        ].filter(_ => _);
        return { i, sku: skus[i], product, current, data: dataWithPrice, changes: pickBy(dataWithPrice, (v, k) => !isEqual(v, current[k])), errors, };
      }));
    } catch(e) {
      console.error(e);
      toast.error('CSVの読み込みに失敗しました');
    }
    toggleValidating(false);
    target.value = '';
  };
  const errorItems = (items || []).filter(_ => !isEmpty(_.errors));
  const changedItems = (items || []).filter(_ => isEmpty(_.errors) && !isEmpty(_.changes));
  const isSubmittable = items != null && isEmpty(errorItems) && changedItems.length > 0 && !isSubmitting;
  const onSubmit = async () => {
    toggleSubmitting(true);
    try {
      await batch(db, changedItems, (batch, { product, changes }) => batch.update(product.ref, changes), 400);
      toast.success(`${changedItems.length}件の商品を更新しました`);
      onFinish && await onFinish();
      onClickClose();
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
      toggleSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} size="xl">
      <ModalHeader>CSV一括更新</ModalHeader>
      <ModalBody>
        <ol className="pl-3">
          <li>現在の絞り込み結果をエクスポート</li>
          <li>変更したい箇所を書き換え</li>
          <li>CSVを選択し、内容を確認して更新</li>
        </ol>
        <div className="small text-muted mb-2">
          SKUで商品を特定します（SKUは変更できません）。空欄にした項目は空になります。列ごと削除した項目は変更されません。
        </div>
        <div className="d-flex gap-1 align-items-center mb-3">
          <ExportButton fileName="商品マスタ_一括更新.csv" label={`エクスポート (${exportTargetProducts.length}件)`} rows={rowsForExport} />
          <Input type="file" accept=".csv" onChange={onSelectFile} disabled={isValidating || isSubmitting} style={{ width: 'auto' }} />
        </div>
        {isValidating && <div className="text-muted"><span className="fas fa-spin fa-spinner mr-1" />検証中…</div>}
        {
          items != null && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>{fileName}（{items.length}行）</strong>
                <span className={isEmpty(errorItems) ? 'text-success' : 'text-danger'}>
                  {isEmpty(errorItems) ? `更新対象 ${changedItems.length}件` : `${errorItems.length}件のエラー`}
                </span>
              </div>
              <div style={{ maxHeight: 480, overflowY: 'auto' }} className="border rounded">
                <table className="table table-sm mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>#</th>
                      <th style={{ minWidth: 100 }}>SKU</th>
                      {values(columns).map(_ => <th key={_} style={{ minWidth: 100 }}>{_}</th>)}
                      <th style={{ minWidth: 100 }}>状態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      items.map(({ i, sku, current, data, changes, errors }) => {
                        const hasErrors = !isEmpty(errors);
                        return (
                          <tr key={i} className={hasErrors ? 'table-danger' : ''}>
                            <td>{i + 1}</td>
                            <td>{sku}</td>
                            {
                              keys(columns).map((k) => (
                                <td key={k}>
                                  {
                                    !hasErrors && has(changes, k) ? (
                                      <span>
                                        <del className="text-muted">{formatValue(k, current[k]) || '(空)'}</del>
                                        <span className="mx-1">→</span>
                                        <span className="text-primary font-weight-bold">{formatValue(k, data[k]) || '(空)'}</span>
                                      </span>
                                    ) : (
                                      formatValue(k, has(data, k) ? data[k] : current[k])
                                    )
                                  }
                                </td>
                              ))
                            }
                            <td className="small text-nowrap">
                              {
                                hasErrors ? (
                                  <span className="text-danger">{errors.map(_ => <div key={_}>{_}</div>)}</span>
                                ) : isEmpty(changes) ? (
                                  <span className="text-muted">変更なし</span>
                                ) : (
                                  <span className="text-success">更新</span>
                                )
                              }
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
              {!isEmpty(errorItems) && <div className="text-danger small mt-2">エラーがあるため更新できません。</div>}
            </div>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClickClose} disabled={isSubmitting}>閉じる</Button>
        <Button color="primary" onClick={onSubmit} disabled={!isSubmittable}>
          {isSubmitting && <span className="fas fa-spin fa-spinner mr-1" />}
          {changedItems.length > 0 ? `${changedItems.length}件を更新` : '更新'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
